import {ContentEditableTextEditor, createContentEditableTextEditor} from '@frontapp/editor-html';
import _, {difference, filter, isString, isUndefined} from 'lodash';
import memoize from 'lru-memoize';
import React, {Component} from 'react';
import styled, {css} from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {addQueryToPath} from '../../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {
  Handler,
  HandlerOf,
  PredicateOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  buildCancelTokenSource,
  CancelError,
  CancelToken,
  CancelTokenSource,
} from '../../../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {DropdownList} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {Loader} from '../../../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {doesTeammateMatchQuery} from '../../../../../../helpers/modelHelpers';
import {isValidMention} from '../../../../../../helpers/models/commentHelpers';
import {isTeammateNamespace} from '../../../../../../helpers/models/namespaceHelpers';
import {emailRecipientParser} from '../../../../../../helpers/models/recipientParsers';
import {
  buildTeammateSearchUrl,
  SearchTeammateSortsEnum,
} from '../../../../../../helpers/models/teammateHelpers';
import {CompanyModel} from '../../../../../../models/api/companyModel';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {RecipientContactMatchModel} from '../../../../../../models/api/recipientMatchModel';
import {RecipientRolesEnum} from '../../../../../../models/api/recipientModel';
import {ApiSearchTeammates} from '../../../../../../models/api/searchTeammateModel';
import {
  TeammateAccountStatusesEnum,
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../../../../../../models/api/teammateModel';
import {baseEmojiValidationMap} from '../../../../../../services/emojis/emojis';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {fetchRecipientContactMatchesAsync} from '../../../../../../services/recipientMatches';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../services/tracking';
import {FindOnlineStatus} from '../../../../../../store/data/teammates/onlineTeammatesSelectors';
import {DropdownCategory} from '../../../../../common/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../../../common/dropdowns/dropdownEmpty';
import {DropdownVirtualList} from '../../../../../common/dropdowns/dropdownVirtualList';
import {ChangeManager} from '../../../../../common/editor/change/changeManager';
import {TextEditorChangeManager} from '../../../../../common/editor/change/textEditorChangeManager';
import {ContentEditable} from '../../../../../common/editor/contentEditable';
import {EmojiEditorDropdown} from '../../../../../common/editor/dropdowns/emojiEditorDropdown';
import {
  createEmojiPlugin,
  EmojiPluginRenderModesEnum,
} from '../../../../../common/editor/plugins/text/emojiPlugin';
import {createMentionPlugin} from '../../../../../common/editor/plugins/text/mentionPlugin';
import {ServiceSubscription} from '../../../../../common/editor/serviceSubscription';
import {InviteDropdownInviteFooter} from './inviteDropdownInviteFooter';
import {InviteDropdownInviteHeader} from './inviteDropdownInviteHeader';
import {InviteDropdownInviteItem} from './inviteDropdownInviteItem';
import {InviteDropdownVisitorItem} from './inviteDropdownVisitorItem';
import {VisualConversationTrackerModel} from './inviteHelpers';

/*
 * Constants.
 */

const resultsLimit = 7;
const maxContactResults = 4;

/*
 * Props.
 */

export interface InviteDropdownInvitePublicProps {
  company: CompanyModel;
  teammates: ReadonlyArray<TeammateModel>;
  mentionableEmails: Set<string>;
  mentionableAliases: Set<string>;
  teammateSuggestions: ReadonlyArray<TeammateModel>;
  isTeamMember: PredicateOf<TeammateModel>;
  findOnlineStatus: FindOnlineStatus;
  addTrackers: (
    trackers: ReadonlyArray<number | string>,
    commentText: string | undefined,
    trackerTeammates: ReadonlyArray<TeammateModel>,
  ) => void;
  onCancelClick: Handler;
  onRequestClose: Handler;
  canInviteVisitors: boolean;
  canAutocompleteMentionContacts: boolean;
  canComment: boolean;
  hasLazyTeammatesDropdowns: boolean;
  user: TeammateModel;
  conversationInboxIds: ReadonlyArray<number>;
  existingTrackers: ReadonlyArray<VisualConversationTrackerModel>;
  hasAutoShareDrafts: boolean;
  conversation: ConversationModel | undefined;
}

interface InviteDropdownInviteProps extends InviteDropdownInvitePublicProps {
  onHasCommentChange: HandlerOf<boolean>;
}

/*
 * Style.
 */

const mentionClassName = 'front-decoration-mention';

const StyledListDiv = styled.div`
  height: 260px;
`;

const StyledEmptyDiv = styled.div`
  height: 100%;
  ${center()};
`;

const StyledWarningDiv = styled.div`
  padding: 8px 12px;

  background: ${(p) => p.theme.greys.shade10};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.tiny};
  line-height: 15px;
`;

const StyledCommentWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledCommentDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  line-height: 20px;
`;

const editorStyle = css`
  padding: 12px;
`;

const StyledContentEditable = styled(ContentEditable)`
  height: 100%;
  ${editorStyle};

  .${mentionClassName} {
    color: ${(p) => p.theme.interactiveText};
    font-weight: ${fontWeights.medium};
  }
`;

const StyledPlaceholderDiv = styled.div`
  position: absolute;
  ${editorStyle};
  pointer-events: none;
  color: ${(p) => p.theme.greys.shade50};
`;

/*
 * Component.
 */

type InviteDropdownTracker = TeammateModel | string;

enum SearchResultTypesEnum {
  TEAMMATE,
  CONTACT,
}

interface TeammateSearchResult {
  type: SearchResultTypesEnum.TEAMMATE;
  teammate: TeammateModel;
}

interface ContactSearchResult {
  type: SearchResultTypesEnum.CONTACT;
  contact: RecipientContactMatchModel;
}

interface SearchResults {
  results: ReadonlyArray<TeammateSearchResult | ContactSearchResult>;
  query: string;
}

interface InviteDropdownInviteState {
  trackers: ReadonlyArray<InviteDropdownTracker>;
  isSearchFocused: boolean;
  searchText: string;
  hasComment: boolean;
  searchResults: SearchResults;
  isLoading: boolean;
}

export class InviteDropdownInvite extends Component<InviteDropdownInviteProps, InviteDropdownInviteState> {
  constructor(props: InviteDropdownInviteProps) {
    super(props);

    this.state = {
      trackers: [],
      isSearchFocused: false,
      searchText: '',
      hasComment: false,
      searchResults: {
        results: [],
        query: '',
      },
      isLoading: true,
    };

    this.editor = createContentEditableTextEditor({
      plugins: [
        createEmojiPlugin({
          renderMode: EmojiPluginRenderModesEnum.SHORTCODE,
          shouldReplaceEmoticons: true,
          emojiValidationMap: baseEmojiValidationMap,
        }),
        createMentionPlugin({
          className: mentionClassName,
          isValidMention: (mention) => isValidMention(this.props.mentionableAliases, mention),
        }),
      ],
      shouldNativeDelete: true,
    });

    this.changeManager = new TextEditorChangeManager(this.editor);
  }

  private readonly editor: ContentEditableTextEditor;
  private readonly changeManager: ChangeManager;
  private fetchCancelToken?: CancelTokenSource;

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.changeManager.subscribe(() => {
      const newHasComment = !this.editor.services.empty.isEmpty;

      this.setState(({hasComment}) => {
        if (newHasComment === hasComment) {
          return null;
        }

        this.props.onHasCommentChange(newHasComment);
        return {hasComment: newHasComment};
      });
    });

    // Fetch the search results to populate the suggestions.
    this.maybeUpdateSearchResultsAsync();
  }

  componentWillUnmount() {
    this.changeManager.unsubscribe();

    this.maybeCancel();
  }

  /*
   * Event handlers.
   */

  private readonly onSearchTextChange: HandlerOf<string> = (searchText) => {
    this.setState({searchText, isLoading: true}, () => this.maybeUpdateSearchResultsAsync());
  };

  private readonly insertTracker: HandlerOf<TeammateModel | string> = (tracker) => {
    this.setState(({trackers}) => ({
      trackers: [...trackers, tracker],
      searchText: '',
    }));
  };

  private readonly removeTrackers: HandlerOf<ReadonlyArray<InviteDropdownTracker>> = (trackersToRemove) => {
    this.setState(({trackers}) => ({
      trackers: difference(trackers, trackersToRemove),
    }));
  };

  private readonly onInviteClick = () => {
    const {addTrackers, onRequestClose, onHasCommentChange} = this.props;
    const {trackers} = this.state;
    const {text} = this.editor.services.text;

    // Add the new trackers to the conversation.
    const trackerIds = trackers.map((t) => (isTeammateTracker(t) ? t.id : t));
    const trackerTeammates = filter(trackers, isTeammateTracker);
    addTrackers(trackerIds, text, trackerTeammates);

    // Close the popup.
    onHasCommentChange(false);
    onRequestClose();

    // Tracking.
    trackInterfaceEvent(InterfaceEventsEnum.SHARE_MENU, {step: 'invite_teammates'});
  };

  /*
   * Render.
   */

  render() {
    const {onRequestClose} = this.props;

    const keyHandlers = {
      [KeyboardActionsEnum.SEND_AND_DONE]: this.onInviteClick,
      [KeyboardActionsEnum.CLOSE]: () => {
        if (this.state.trackers.length === 0 && onRequestClose) {
          onRequestClose();
        }
      },
    };

    return (
      <KeyboardShortcuts handlers={keyHandlers}>
        {this.renderHeader()}
        <StyledListDiv>{this.renderList()}</StyledListDiv>
        {this.renderFooter()}
      </KeyboardShortcuts>
    );
  }

  /*
   * Header.
   */

  private renderHeader() {
    const {trackers, searchText} = this.state;
    return (
      <InviteDropdownInviteHeader
        trackers={trackers}
        searchText={searchText}
        onSearchTextChange={this.onSearchTextChange}
        onSearchFocus={() => this.setState({isSearchFocused: true})}
        onSearchBlur={() => this.setState({isSearchFocused: false})}
        removeTracker={this.removeTrackers}
        canInviteVisitors={this.props.canInviteVisitors}
      />
    );
  }

  /*
   * Results.
   */

  private renderList() {
    const {isSearchFocused, searchText, trackers} = this.state;
    const {canComment} = this.props;
    const query = searchText.trim();

    // If we have a query, display the search results.
    if (query && isSearchFocused) {
      return this.renderSearchResults();
    }

    // If we have trackers, display the comment box.
    if (canComment && trackers.length > 0) {
      return this.renderInviteComment();
    }

    // Otherwise, display suggestions.
    return this.renderSearchResults();
  }

  private renderInviteComment() {
    const {trackers} = this.state;
    const hasVisitors = trackers.some(isVisitorTracker);
    const {hasAutoShareDrafts, conversation} = this.props;
    const shouldShowDraftWarning =
      hasAutoShareDrafts && conversation && isTeammateNamespace(conversation.namespace);

    return (
      <StyledCommentWrapperDiv>
        {hasVisitors && (
          <StyledWarningDiv>
            <Trans id="invite-visitorswarning">
              External guests can read the conversation, post comments, and edit drafts.
            </Trans>
          </StyledWarningDiv>
        )}
        {shouldShowDraftWarning && (
          <StyledWarningDiv>
            <Trans id="invite-draftswarning">Drafts are viewable and editable by all participants.</Trans>
          </StyledWarningDiv>
        )}
        <StyledCommentDiv>
          <ServiceSubscription source={this.editor.services.empty}>
            {({isEmpty}) => isEmpty && renderPlaceholder()}
          </ServiceSubscription>
          <StyledContentEditable editor={this.editor} />
        </StyledCommentDiv>
        <EmojiEditorDropdown autocompleteService={this.editor.services.autocomplete} />
      </StyledCommentWrapperDiv>
    );
  }

  // Update the search results by either removing the user or adding the user depending on subscription status.
  private computeResultsBasedOnUserSubscriptionStatus() {
    const {user, existingTrackers} = this.props;
    const {searchResults} = this.state;
    const {results} = searchResults;

    const userAsTracker = existingTrackers.find(({teammate}) => teammate.id === user.id);
    const isUserSubscriber = Boolean(userAsTracker && !userAsTracker.hasMuted);

    const isUserInResults = results.find(
      (result) => result.type === SearchResultTypesEnum.TEAMMATE && result.teammate.id === user.id,
    );

    // If user is a subscriber and in results, remove user as a suggestion.
    if (isUserSubscriber && isUserInResults) {
      return results.filter((result) => {
        if (result.type === SearchResultTypesEnum.TEAMMATE) {
          return result.teammate.id !== user.id;
        }
        return true;
      });
    }

    // If user isn't a subscriber and isn't already in results, append user as a suggestion to the front of array.
    if (!isUserSubscriber && !isUserInResults) {
      const userTeammateSearchResult: TeammateSearchResult = {
        type: SearchResultTypesEnum.TEAMMATE,
        teammate: user,
      };
      return [userTeammateSearchResult, ...results];
    }

    return results;
  }

  private renderTeammateSuggestions() {
    const {isLoading} = this.state;

    const results = this.computeResultsBasedOnUserSubscriptionStatus();

    if (results.length === 0 && isLoading) {
      // If there's no suggestions and we're loading, render a spinner.
      return renderLoading();
    }

    if (results.length === 0) {
      // If there are no suggestions, render an empty state.
      return (
        <StyledEmptyDiv>
          <DropdownEmpty>
            <Trans id="invite-noteammates">There are no teammates to invite</Trans>
          </DropdownEmpty>
        </StyledEmptyDiv>
      );
    }

    // Render the suggestions.
    return (
      <DropdownList>
        <DropdownCategory>
          <Trans id="inline-suggestions">Suggestions</Trans>
        </DropdownCategory>
        {results.map((result) => this.renderTeammateOrContact(result))}
      </DropdownList>
    );
  }

  private renderSearchResults() {
    const {searchResults, isLoading} = this.state;
    const {results, query} = searchResults;

    if (!query) {
      // If we have no query, the results are suggestions.
      return this.renderTeammateSuggestions();
    }

    if (results.length === 0) {
      return isLoading ? renderLoading() : this.renderEmpty(query);
    }

    return (
      <DropdownVirtualList
        key={query}
        width={320}
        maxHeight={260}
        minHeight={260}
        rowCount={results.length}
        renderRow={(index) => this.renderTeammateOrContact(results[index])}
        shouldSelectFirst={query.length > 0}
      />
    );
  }

  private renderTeammateOrContact(searchResult: TeammateSearchResult | ContactSearchResult) {
    if (searchResult.type === SearchResultTypesEnum.TEAMMATE) {
      return (
        <InviteDropdownInviteItem
          key={searchResult.teammate.id}
          teammate={searchResult.teammate}
          isTeamMember={this.props.isTeamMember(searchResult.teammate)}
          onlineStatus={this.props.findOnlineStatus(searchResult.teammate)}
          onSelect={() => this.insertTracker(searchResult.teammate)}
        />
      );
    }

    return (
      <InviteDropdownVisitorItem
        key={searchResult.contact.handle}
        email={searchResult.contact.handle}
        onSelect={() => this.insertTracker(searchResult.contact.handle)}
      />
    );
  }

  private readonly renderEmpty = (query: string) => {
    const {canInviteVisitors} = this.props;
    const recipientFromQuery = canInviteVisitors && emailRecipientParser(RecipientRolesEnum.MENTION, query);
    if (recipientFromQuery && !this.state.trackers.includes(recipientFromQuery.contact.handle)) {
      return (
        <DropdownList shouldSelectFirst>
          <InviteDropdownVisitorItem
            email={recipientFromQuery.contact.handle}
            onSelect={() => this.insertTracker(recipientFromQuery.contact.handle)}
          />
        </DropdownList>
      );
    }

    return (
      <StyledEmptyDiv>
        <DropdownEmpty />
      </StyledEmptyDiv>
    );
  };

  private async maybeUpdateSearchResultsAsync() {
    const {hasLazyTeammatesDropdowns} = this.props;
    const {searchText} = this.state;

    // Cancel previous request if any and create a new cancel token.
    this.maybeCancel();
    this.fetchCancelToken = buildCancelTokenSource();
    const {token} = this.fetchCancelToken;

    const query = searchText.trim();

    const results = hasLazyTeammatesDropdowns
      ? await this.fetchSearchResultsForLazyTeammatesAsync(query, token)
      : await this.fetchSearchResultsAsync(query, token);
    if (isUndefined(results)) {
      return;
    }

    this.setState({
      searchResults: {
        results,
        query,
      },
      isLoading: false,
    });
  }

  private async fetchSearchResultsAsync(query: string, cancelToken: CancelToken) {
    const {company, teammates, mentionableEmails, canAutocompleteMentionContacts, teammateSuggestions} =
      this.props;
    const {trackers} = this.state;

    if (!query) {
      return _(teammateSuggestions)
        .filter((t) => !trackers.includes(t))
        .take(4)
        .map<TeammateSearchResult>((teammate) => ({
          type: SearchResultTypesEnum.TEAMMATE,
          teammate,
        }))
        .value();
    }

    // If teammates are enough, return early.
    const filteredTeammates = filterTeammates(teammates, trackers, query, resultsLimit);
    const teammateResults = filteredTeammates.map<TeammateSearchResult>((teammate) => ({
      type: SearchResultTypesEnum.TEAMMATE,
      teammate,
    }));

    // Check if we have enough teammates, or if we need contacts on top.
    if (filteredTeammates.length >= resultsLimit || !canAutocompleteMentionContacts) {
      return teammateResults;
    }

    // List the email trackers.
    const visitorTrackers = trackers.filter(isVisitorTracker);

    // Otherwise, fetch additional contacts.
    try {
      // Maybe update the collection of emails to exclude.
      const updatedExcludedEmails = new Set([...mentionableEmails, ...visitorTrackers]);

      // Fetch enough to reach the overall limit.
      const contactResultsLimit = Math.min(maxContactResults, resultsLimit - filteredTeammates.length);
      const contacts = await fetchRecipientContactMatchesAsync(company.url, query, {
        limit: contactResultsLimit,
        excludeEmails: updatedExcludedEmails,
        cancelToken,
      });
      const contactResults = contacts.map<ContactSearchResult>((contact) => ({
        type: SearchResultTypesEnum.CONTACT,
        contact,
      }));

      return [...teammateResults, ...contactResults];
    } catch (error) {
      if (!(error instanceof CancelError)) {
        throw error;
      }
    }
    return undefined;
  }

  private async fetchSearchResultsForLazyTeammatesAsync(query: string, cancelToken: CancelToken) {
    const {company, mentionableEmails, canAutocompleteMentionContacts, user, conversationInboxIds} =
      this.props;
    const {trackers} = this.state;

    try {
      if (!query) {
        return await fetchSuggestionsAsync({
          cancelToken,
          userUrl: user.url,
          conversationInboxIds,
        });
      }

      // Fetch teammates.
      const url = buildTeammateSearchUrl(company.url, query, {
        limit: resultsLimit,
        accountStatuses: [TeammateAccountStatusesEnum.ACTIVE],
        licenseTypes: [TeammateLicenseTypesEnum.FULL],
        excludedTeammateIds: [user.id],
        sort: SearchTeammateSortsEnum.NAME,
      });
      const teammates = await fetchApiAsync(ApiSearchTeammates, url, {cancelToken});
      const teammateResults = teammates.teammates.map<TeammateSearchResult>((teammate) => ({
        type: SearchResultTypesEnum.TEAMMATE,
        teammate,
      }));

      // Check if we have enough teammates, or if we need contacts on top.
      if (teammateResults.length >= resultsLimit || !canAutocompleteMentionContacts) {
        return teammateResults;
      }

      // Otherwise, fetch additional contacts.

      // List the email trackers.
      const visitorTrackers = trackers.filter(isVisitorTracker);

      // Maybe update the collection of emails to exclude.
      const updatedExcludedEmails = new Set([...mentionableEmails, ...visitorTrackers]);

      // Fetch enough to reach the overall limit.
      const contactResultsLimit = Math.min(maxContactResults, resultsLimit - teammateResults.length);
      const contacts = await fetchRecipientContactMatchesAsync(company.url, query, {
        limit: contactResultsLimit,
        excludeEmails: updatedExcludedEmails,
        cancelToken,
      });
      const contactResults = contacts.map<ContactSearchResult>((contact) => ({
        type: SearchResultTypesEnum.CONTACT,
        contact,
      }));

      return [...teammateResults, ...contactResults];
    } catch (error) {
      if (!(error instanceof CancelError)) {
        throw error;
      }
    }
    return undefined;
  }

  private maybeCancel() {
    // Check if we have a request in progress to cancel.
    if (this.fetchCancelToken) {
      this.fetchCancelToken.cancel();
    }
  }

  /*
   * Footer.
   */

  private renderFooter() {
    return (
      <InviteDropdownInviteFooter
        onCancelClick={this.props.onCancelClick}
        onInviteClick={this.onInviteClick}
        isInviteEnabled={this.state.trackers.length > 0}
        hasComment={this.state.hasComment}
      />
    );
  }
}

function renderPlaceholder() {
  return (
    <StyledPlaceholderDiv>
      <Trans id="invite-placeholder">Add a comment (optional)</Trans>
    </StyledPlaceholderDiv>
  );
}

function renderLoading() {
  return (
    <StyledEmptyDiv>
      <Loader size={VisualSizesEnum.SMALL} />
    </StyledEmptyDiv>
  );
}

const filterTeammates = memoize(20)(
  (
    teammates: ReadonlyArray<TeammateModel>,
    trackers: ReadonlyArray<InviteDropdownTracker>,
    query: string,
    limit: number,
  ) => {
    // Shortcut for empty query.
    if (!query) {
      return teammates;
    }

    // Otherwise, filter by name.
    return _(teammates)
      .filter((t) => !trackers.includes(t) && doesTeammateMatchQuery(t, query))
      .take(limit)
      .value();
  },
);

function isTeammateTracker(tracker: InviteDropdownTracker): tracker is TeammateModel {
  return !isString(tracker);
}

function isVisitorTracker(tracker: InviteDropdownTracker): tracker is string {
  return isString(tracker);
}

type SuggestionsFetchOptions = {
  cancelToken: CancelToken;
  conversationInboxIds: ReadonlyArray<number>;
  userUrl: string;
};

async function fetchSuggestionsAsync(options: SuggestionsFetchOptions) {
  if (options.conversationInboxIds.length === 0) {
    // We don't have inbox ids to fetch the recent suggestions for.
    return [];
  }

  const recentAssigneesUrl = addQueryToPath(`${options.userUrl}/recent_assignees`, {
    inbox_ids: options.conversationInboxIds.join(','),
  });
  const searchTeammates = await fetchApiAsync(ApiSearchTeammates, recentAssigneesUrl, {
    cancelToken: options.cancelToken,
  });
  return searchTeammates.teammates.map<TeammateSearchResult>((teammate) => ({
    type: SearchResultTypesEnum.TEAMMATE,
    teammate,
  }));
}
